import React from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  MenuItem
} from '@material-ui/core';

import NotyHelper from '../api/NotyHelper';

const API_URL = process.env.REACT_APP_API_URL;

const getAccessToken = () =>
  JSON.parse(JSON.parse(localStorage.getItem('persist:root')).auth).accessToken;

const filenameFromDisposition = (disposition, fallback) => {
  if (!disposition || !disposition.includes('filename=')) return fallback;
  const idx = disposition.indexOf('filename=');
  return disposition
    .substring(idx + 9)
    .split(';')[0]
    .replace(/['"]/g, '')
    .trim();
};

const triggerDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

// Shared dialog: pick a print template group, POST it to `endpoint`, download
// the returned file. `buildBody` maps the chosen group id to the request body.
const PrintTemplateGroupDialog = ({
  open,
  onClose,
  printTemplateGroups,
  endpoint,
  buildBody,
  defaultFilename,
  errorMessage = 'Failed to generate file'
}) => (
  <Formik
    enableReinitialize
    initialValues={{ templateGroup: '' }}
    onSubmit={async (values, { setSubmitting }) => {
      try {
        const response = await fetch(`${API_URL}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAccessToken()}`
          },
          body: JSON.stringify(buildBody(values.templateGroup))
        });
        if (!response.ok) throw new Error(errorMessage);
        const filename = filenameFromDisposition(
          response.headers.get('Content-Disposition'),
          defaultFilename
        );
        triggerDownload(await response.blob(), filename);
        onClose();
      } catch (error) {
        console.error(`${errorMessage}:`, error);
        NotyHelper.error(errorMessage);
      } finally {
        setSubmitting(false);
      }
    }}
    validationSchema={Yup.object().shape({
      templateGroup: Yup.number().integer().required('Print template group is required')
    })}
  >
    {({ values, errors, touched, handleChange, handleSubmit, isSubmitting }) => (
      <Dialog
        open={open}
        onClose={(_, reason) => {
          // Block backdrop/escape close while submitting so the in-flight
          // request doesn't unmount its own setState target.
          if (isSubmitting && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
          onClose();
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogContent>
          <form onSubmit={handleSubmit} autoComplete="off">
            <TextField
              select
              fullWidth
              label="Print template group"
              variant="outlined"
              margin="normal"
              name="templateGroup"
              value={values.templateGroup}
              onChange={handleChange}
              disabled={isSubmitting}
              error={touched.templateGroup && Boolean(errors.templateGroup)}
              helperText={touched.templateGroup && errors.templateGroup}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {printTemplateGroups && printTemplateGroups.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}{' '}
                  {option.description ? `(${option.description})` : ''}
                </MenuItem>
              ))}
            </TextField>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            autoFocus
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isSubmitting ? 'Printing…' : 'Ok'}
          </Button>
        </DialogActions>
      </Dialog>
    )}
  </Formik>
);

export default PrintTemplateGroupDialog;
